import { cva } from 'class-variance-authority';

/** Inline note diff token: grey struck-out deletions, green additions that stay marked until the user clicks the note. */
export const diffSegmentVariants = cva('box-decoration-clone transition-colors duration-500', {
  variants: {
    kind: {
      equal: '',
      // Gap only where an addition directly replaces a deletion, so struck and new text don't touch.
      add: 'rounded-[3px] bg-green-100 text-green-900 ring-1 ring-green-700/20 [[data-diff=del]+&]:ml-1',
      settledAdd: 'rounded-[3px] bg-green-100 ring-1 ring-green-700/10',
      del: 'text-slate-400 line-through decoration-slate-400',
    },
  },
  defaultVariants: { kind: 'equal' },
});

export const composerShellClasses =
  'flex items-end gap-1.5 rounded-2xl border border-slate-200 bg-white py-1.5 pr-1.5 pl-3.5 shadow-sm transition-shadow focus-within:border-primary/40 focus-within:shadow-md';
export const composerTextareaClasses =
  'field-sizing-content max-h-40 min-h-[40px] flex-1 resize-none border-0 bg-transparent px-0 py-2.5 text-sm shadow-none focus-visible:ring-0';
export const dictationButtonClasses = 'h-9 w-9 rounded-xl text-muted-foreground';
export const dictationActiveClasses =
  'bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive';
export const composerCaptionClasses = 'mt-1.5 text-center text-[11px] text-muted-foreground';
export const composerReplyClasses = 'mt-1.5 block text-center text-slate-600 text-xs';
export const composerErrorClasses = 'mt-1.5 text-destructive text-xs';
export const suggestionChipClasses =
  'shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700 text-xs transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50';
export const ledgerShellClasses = 'mb-2 rounded-xl border border-green-200 bg-green-50/60';
export const latestChangeUndoClasses =
  'h-6 shrink-0 gap-1 px-2 font-medium text-green-800 text-xs hover:bg-green-100 hover:text-green-900';
export const ledgerToggleClasses =
  'flex h-6 shrink-0 cursor-pointer items-center gap-0.5 rounded-md pr-1 pl-1.5 text-slate-500 hover:bg-green-100 disabled:cursor-default disabled:hover:bg-transparent';
export const ledgerCountClasses = 'flex h-5 items-center rounded-full px-2 font-semibold text-[11px]';
export const ledgerUndoClasses =
  'h-6 shrink-0 gap-1 rounded-full border-slate-200 px-2.5 font-medium text-slate-600 text-xs hover:text-slate-900';
export const ledgerTaskLinkClasses = 'h-auto p-0 font-medium text-xs';
export const noteDiffClasses =
  'block w-full cursor-text whitespace-pre-wrap text-left font-sans text-[15px] text-slate-700 leading-relaxed';
/** The composer panel's note: on the page itself, no card. */
export const noteTextClasses =
  'min-h-24 cursor-text whitespace-pre-wrap rounded-sm font-sans text-[15px] text-slate-700 leading-relaxed outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-4';
/** Editing: a faint tinted, ringed box; the negative margin and matching padding keep the text where it was. */
export const noteTextareaClasses =
  'field-sizing-content -mx-2 -my-1 min-h-24 w-[calc(100%+1rem)] resize-none rounded-md border-0 bg-primary/[0.03] px-2 py-1 font-sans text-[15px] text-slate-700 leading-relaxed shadow-none ring-1 ring-primary/25 focus-visible:ring-1 focus-visible:ring-primary/25 md:text-[15px]';
/** Classic underline tabs for the composer panel, on the header band. */
export const noteTabListClasses = 'flex h-auto w-full justify-start gap-6 rounded-none bg-transparent p-0';
export const noteTabClasses =
  'rounded-none border-transparent border-b-2 px-1 pt-1 pb-2.5 text-slate-500 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';
// One slate-50 band with the tabs above it, one line under it; h-12 keeps the band at 2 × h-12.
export const noteToolbarClasses =
  'flex h-12 min-w-0 shrink-0 items-center gap-1 border-border border-b bg-slate-50 px-4';
export const noteTemplateTriggerClasses =
  'h-8 min-w-0 flex-1 justify-start gap-2 bg-white text-sm [&>span]:flex-1 [&>span]:truncate [&>span]:text-left';
export const highlightedTaskClasses = 'border-green-500 ring-2 ring-green-200';
/** Green count for "something just changed": the Breytingar count and the Verkefni tab badge. */
export const activeCountClasses = 'bg-green-100 text-green-800';
